/**
 * Phase configuration mappings for PilotCity authentication flow
 * Defines behavior, navigation rules, and visual styling for all auth phases
 */

import {
  PhaseCategory,
  type AuthPhaseConfig,
  type PhaseCondition,
  ConditionalTrigger
} from '@/types/auth-phases';
import type { AuthPhase } from '@/composables/useAuthOrchestrator';

/**
 * Conditional phase visibility logic
 */
const conditionalPhaseConditions: Record<string, PhaseCondition> = {
  accountDiscovery: state => {
    // Show if encountered OR if we have a discovered account
    return (
      state.encounteredPhases.has('accountDiscovery') ||
      state.conditionalTriggers.has(ConditionalTrigger.ACCOUNT_DISCOVERY)
    );
  },

  identityConflict: state => {
    // Show if encountered OR if we have an identity conflict
    return (
      state.encounteredPhases.has('identityConflict') ||
      state.conditionalTriggers.has(ConditionalTrigger.IDENTITY_CONFLICT)
    );
  },

  identityLinkingOtp: state => {
    // Show if encountered OR if we're in an identity linking flow
    return (
      state.encounteredPhases.has('identityLinkingOtp') ||
      state.conditionalTriggers.has(ConditionalTrigger.DUPLICATE_EMAIL) ||
      state.conditionalTriggers.has(ConditionalTrigger.DUPLICATE_PHONE)
    );
  }
};

/**
 * Complete phase configuration for all authentication phases
 */
export const authPhaseConfigs: Record<AuthPhase, AuthPhaseConfig> = {
  // Initial phases
  idle: {
    phase: 'idle',
    category: PhaseCategory.REQUIRED,
    label: 'Idle',
    description: 'Authentication not started',
    showInStepper: false,
    allowBackNavigation: false
  },

  contact: {
    phase: 'contact',
    category: PhaseCategory.REQUIRED,
    label: 'Contact',
    description: 'Enter email or phone number',
    icon: 'mdi-account-plus',
    showInStepper: false,
    allowBackNavigation: false
  },

  otp: {
    phase: 'otp',
    category: PhaseCategory.REQUIRED,
    label: 'Verify',
    description: 'Enter verification code',
    icon: 'mdi-shield-check',
    showInStepper: true,
    allowBackNavigation: false, // Prevent going back to OTP after verification
    isOneWayGate: true, // Once verified, cannot return to this phase
    color: 'primary'
  },

  password: {
    phase: 'password',
    category: PhaseCategory.OPTIONAL,
    label: 'Password',
    description: 'Enter existing password',
    icon: 'mdi-lock',
    showInStepper: false,
    allowBackNavigation: false, // Prevent going back to password after login
    isOneWayGate: true // Once authenticated, cannot return to this phase
  },

  passwordSignup: {
    phase: 'passwordSignup',
    category: PhaseCategory.OPTIONAL,
    label: 'Create Password',
    description: 'Create a password for new account',
    icon: 'mdi-lock-plus',
    showInStepper: true,
    allowBackNavigation: false, // Prevent going back to password signup after completion
    isOneWayGate: true // Once password is set, cannot return to this phase
  },

  setPassword: {
    phase: 'setPassword',
    category: PhaseCategory.OPTIONAL,
    label: 'Set Password',
    description: 'Create a new password',
    icon: 'mdi-lock-plus',
    showInStepper: true,
    allowBackNavigation: true
  },

  alternateContact: {
    phase: 'alternateContact',
    category: PhaseCategory.OPTIONAL,
    label: 'Add Contact',
    description: 'Add alternate email or phone',
    icon: 'mdi-plus-circle',
    showInStepper: true,
    allowBackNavigation: true
  },

  // Profile phases
  generalProfile: {
    phase: 'generalProfile',
    category: PhaseCategory.REQUIRED,
    label: 'Profile',
    description: 'Basic profile information',
    icon: 'mdi-account',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  // Conditional phases (only show when triggered)
  accountDiscovery: {
    phase: 'accountDiscovery',
    category: PhaseCategory.CONDITIONAL,
    label: 'Account Found',
    description: 'Existing account discovered',
    icon: 'mdi-account-search',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'info',
    conditions: conditionalPhaseConditions.accountDiscovery
  },

  identityConflict: {
    phase: 'identityConflict',
    category: PhaseCategory.CRITICAL,
    label: 'Resolve Conflict',
    description: 'Identity conflict resolution',
    icon: 'mdi-account-multiple-check',
    showInStepper: true,
    allowBackNavigation: false, // One-way gate
    requiresConfirmation: true,
    color: 'warning',
    isOneWayGate: true,
    locksPreviousPhases: true,
    conditions: conditionalPhaseConditions.identityConflict
  },

  identityLinkingOtp: {
    phase: 'identityLinkingOtp',
    category: PhaseCategory.CONDITIONAL,
    label: 'Link Account',
    description: 'Verify identity linking',
    icon: 'mdi-link-variant',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'secondary',
    conditions: conditionalPhaseConditions.identityLinkingOtp
  },

  // Role profile phases
  teacherProfile: {
    phase: 'teacherProfile',
    category: PhaseCategory.REQUIRED,
    label: 'Teacher Info',
    description: 'Teacher profile information',
    icon: 'mdi-school',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  studentProfile: {
    phase: 'studentProfile',
    category: PhaseCategory.REQUIRED,
    label: 'Student Info',
    description: 'Student profile information',
    icon: 'mdi-account-school',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  parentProfile: {
    phase: 'parentProfile',
    category: PhaseCategory.REQUIRED,
    label: 'Parent Info',
    description: 'Parent profile information',
    icon: 'mdi-account-child',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  employerProfile: {
    phase: 'employerProfile',
    category: PhaseCategory.REQUIRED,
    label: 'Employer Info',
    description: 'Employer profile information',
    icon: 'mdi-domain',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  schoolProfile: {
    phase: 'schoolProfile',
    category: PhaseCategory.REQUIRED,
    label: 'School Info',
    description: 'School profile information',
    icon: 'mdi-school-outline',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  organizerProfile: {
    phase: 'organizerProfile',
    category: PhaseCategory.REQUIRED,
    label: 'Organizer Info',
    description: 'Organizer profile information',
    icon: 'mdi-account-group',
    showInStepper: true,
    allowBackNavigation: true,
    color: 'primary'
  },

  // Completion phases
  complete: {
    phase: 'complete',
    category: PhaseCategory.REQUIRED,
    label: 'Complete',
    description: 'Authentication completed',
    icon: 'mdi-check-circle',
    showInStepper: false,
    allowBackNavigation: false,
    color: 'success'
  },

  error: {
    phase: 'error',
    category: PhaseCategory.REQUIRED,
    label: 'Error',
    description: 'Authentication error',
    icon: 'mdi-alert-circle',
    showInStepper: false,
    allowBackNavigation: true,
    color: 'error'
  }
};

/**
 * Get phase configuration by phase name
 */
export const getPhaseConfig = (phase: AuthPhase): AuthPhaseConfig => {
  return authPhaseConfigs[phase];
};

/**
 * Get all phases by category
 */
export const getPhasesByCategory = (category: PhaseCategory): AuthPhaseConfig[] => {
  return Object.values(authPhaseConfigs).filter(config => config.category === category);
};

/**
 * Get visible phases for stepper based on current state
 */
export const getVisiblePhases = (state: any): AuthPhaseConfig[] => {
  return Object.values(authPhaseConfigs).filter(config => {
    // Always show required phases that should appear in stepper
    if (config.category === PhaseCategory.REQUIRED && config.showInStepper) {
      return true;
    }

    // Show optional phases that should appear in stepper
    if (config.category === PhaseCategory.OPTIONAL && config.showInStepper) {
      return true;
    }

    // Only show conditional/critical phases if conditions are met
    if (
      config.category === PhaseCategory.CONDITIONAL ||
      config.category === PhaseCategory.CRITICAL
    ) {
      if (!config.showInStepper) return false;

      // Check if phase has been encountered or is currently active
      const isEncountered = state.encounteredPhases?.has(config.phase);
      const isActive = state.phase === config.phase;
      const conditionsMet = config.conditions ? config.conditions(state) : false;

      return isEncountered || isActive || conditionsMet;
    }

    return false;
  });
};

/**
 * Check if a phase should be locked from navigation
 */
export const isPhaseAccessible = (targetPhase: AuthPhase, state: any): boolean => {
  const config = getPhaseConfig(targetPhase);

  // Cannot navigate to locked phases
  if (state.lockedPhases?.has(targetPhase)) {
    return false;
  }

  // Cannot navigate back from one-way gates
  if (config.isOneWayGate && state.encounteredPhases?.has(targetPhase)) {
    return false;
  }

  // Check if back navigation is allowed
  if (!config.allowBackNavigation && state.completedPhases?.has(targetPhase)) {
    return false;
  }

  return true;
};

/**
 * Get phases that should be locked when entering a critical phase
 */
export const getPhasesToLock = (criticalPhase: AuthPhase): AuthPhase[] => {
  const config = getPhaseConfig(criticalPhase);

  if (!config.locksPreviousPhases) {
    return [];
  }

  // For identity conflict, lock general profile and any previous phases
  if (criticalPhase === 'identityConflict') {
    return ['generalProfile', 'alternateContact'];
  }

  return [];
};
